import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

const Toggle = ({ on, onClick, disabled, testId, title }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} title={title} aria-pressed={on}
    style={{ width: 40, height: 22, borderRadius: 999, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      background: on ? '#10B981' : '#CBD5E1', position: 'relative', transition: 'background-color 0.15s ease', flexShrink: 0 }}>
    <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: 999, background: '#fff',
      transition: 'left 0.15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
  </button>
);

const Row = ({ label, value, testId, strong, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
    <span style={{ color: '#64748B' }}>{label}</span>
    <span style={{ color: color || '#0F172A', fontWeight: strong ? 600 : 500 }} data-testid={testId}>{value}</span>
  </div>
);

// Admin-only payment permissions for one proposal: "Allow Partial Payments" (default OFF, percent or fixed $),
// deposit tracking, and invoicing / payment-terms approval (FIU auto, customer-level, or this proposal only).
const PaymentPermissionsPanel = ({ proposal, isAdmin, onUpdated }) => {
  const [perm, setPerm] = useState(null);
  const [ptype, setPtype] = useState('percent');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!proposal?.id) return;
    try {
      const res = await axios.get(`/proposals/${proposal.id}/payment-permissions`);
      setPerm(res.data);
      const pp = res.data.partial_payments || {};
      if (pp.enabled) { setPtype(pp.type || 'percent'); setValue(String(pp.value ?? '')); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load payment permissions');
    }
  }, [proposal?.id]);

  useEffect(() => { load(); }, [load, proposal?.payments?.length, proposal?.total_amount, proposal?.status]);

  if (!perm) return null;
  const pp = perm.partial_payments || {};
  const locked = perm.status === 'paid' || perm.balance_due <= 0;

  const savePartial = async (enabled, type = ptype, val = value) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const res = await axios.patch(`/proposals/${proposal.id}/partial-payments`, { enabled, type, value: enabled ? parseFloat(val) : null });
      setPerm(res.data);
      toast.success(enabled ? 'Partial payments enabled — customer can pay the authorized deposit' : 'Partial payments off — customer must pay the full balance');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update partial payments');
    } finally {
      setSaving(false);
    }
  };

  const togglePartial = () => {
    if (pp.enabled) return savePartial(false);
    const v = parseFloat(value);
    if (!Number.isFinite(v) || v <= 0) { setPtype('percent'); setValue('50'); return savePartial(true, 'percent', '50'); }
    return savePartial(true);
  };

  const toggleProposalTerms = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      await axios.put(`/proposals/${proposal.id}/allow-skip-payment`, { allow_skip_payment: !perm.allow_skip_payment });
      await load();
      toast.success(!perm.allow_skip_payment ? 'This proposal can be accepted without payment (invoiced)' : 'Payment is now required on this proposal');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update payment terms');
    } finally {
      setSaving(false);
    }
  };

  const termsSource = perm.is_fiu ? 'FIU customer — auto-approved (no payment required)'
    : perm.customer_terms?.approved ? `Customer approved for invoicing (${perm.customer_terms.label})`
      : perm.allow_skip_payment ? 'Approved on this proposal only' : 'Not approved — customer must pay';

  return (
    <div style={{ paddingTop: 10, marginBottom: 12, borderTop: '1px solid #F1F5F9' }} data-testid="payment-permissions-panel">
      {/* Partial payments */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 13, color: '#0F172A', fontWeight: 600 }}>Allow Partial Payments</div>
          <div style={{ fontSize: 11, color: '#94A3B8' }}>{pp.enabled ? 'Customer may pay the authorized deposit' : 'Off — customer pays the full balance'}</div>
        </div>
        <Toggle on={!!pp.enabled} onClick={togglePartial} disabled={!isAdmin || saving || locked} testId="toggle-partial-payments"
          title={isAdmin ? 'Admins only: let the customer pay an authorized deposit' : 'Only administrators can change this'} />
      </div>
      {pp.enabled && (
        <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, padding: 8, marginBottom: 8 }}>
          {isAdmin && !locked && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <select value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="partial-type-select"
                style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #CBD5E1', borderRadius: 4, background: '#fff' }}>
                <option value="percent">Percent of total</option>
                <option value="fixed">Fixed amount ($)</option>
              </select>
              <input type="number" step={ptype === 'percent' ? '1' : '0.01'} min="0.01" max={ptype === 'percent' ? 99 : undefined} value={value}
                onChange={(e) => setValue(e.target.value)} data-testid="partial-value-input"
                style={{ width: 90, fontSize: 12, padding: '4px 6px', border: '1px solid #CBD5E1', borderRadius: 4 }} />
              <span style={{ fontSize: 12, color: '#64748B' }}>{ptype === 'percent' ? '%' : 'USD'}</span>
              <button type="button" onClick={() => savePartial(true)} disabled={saving} data-testid="save-partial-payments-btn"
                style={{ fontSize: 12, padding: '4px 10px', border: 'none', borderRadius: 4, background: '#0F172A', color: '#fff', cursor: 'pointer' }}>
                {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
              </button>
            </div>
          )}
          <Row label="Authorized deposit" value={fmt(pp.deposit_required)} testId="deposit-required" strong />
          <Row label="Deposit paid" value={fmt(pp.deposit_paid)} testId="deposit-paid" color="#10B981" />
          <Row label="Customer pays now" value={pp.amount_due_now ? fmt(pp.amount_due_now) : (pp.deposit_met ? 'Deposit met — balance only' : fmt(perm.balance_due))} testId="deposit-due-now" />
          <Row label="Remaining after deposit" value={fmt(Math.max(perm.total_amount - (pp.deposit_required || 0), 0))} testId="remaining-after-deposit" />
          {pp.set_by && <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 4 }}>Set by {pp.set_by}{pp.set_at ? ` · ${new Date(pp.set_at).toLocaleDateString()}` : ''}</div>}
        </div>
      )}

      {/* Payment terms / invoicing */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, color: '#0F172A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            {perm.can_skip_payment && <ShieldCheck size={13} style={{ color: '#2563EB' }} />}No Payment Required
          </div>
          <div style={{ fontSize: 11, color: perm.can_skip_payment ? '#2563EB' : '#94A3B8' }} data-testid="payment-terms-status">{termsSource}</div>
        </div>
        {!perm.is_fiu && !perm.customer_terms?.approved && (
          <Toggle on={!!perm.allow_skip_payment} onClick={toggleProposalTerms} disabled={!isAdmin || saving || perm.status === 'paid'}
            testId="toggle-allow-skip-payment" title={isAdmin ? 'Admins only: let this customer accept and be invoiced' : 'Only administrators can change this'} />
        )}
      </div>
      {perm.accepted_without_payment_reason && (
        <div style={{ fontSize: 11, color: '#2563EB', marginTop: 4 }} data-testid="accepted-without-payment-note">
          Accepted without payment ({perm.accepted_without_payment_reason === 'fiu' ? 'FIU' : perm.accepted_without_payment_reason === 'customer_terms' ? 'customer terms' : 'proposal approval'}).
        </div>
      )}
      {!isAdmin && <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>Only administrators can change payment permissions.</div>}
    </div>
  );
};

export default PaymentPermissionsPanel;
